import React, { useEffect, useState } from 'react';
import { Animated, Image, Text, View, type StyleProp, type TextStyle } from 'react-native';
import type { ChatPreviewMarker, ResolvedChatPreview } from '../../screens/chat/chatListView';
import { MEDIA_PREVIEW_GLYPH, mediaPreviewKind, type MediaPreviewKind } from '../../screens/chat/chatPreviewMedia';
import type { ChatListStyles } from '../../theme/chatListStylesFromPalette';
import { CONTROL } from '../../theme/controlMetrics';
import { darkSkinGifSource } from '../../util/darkSkinSmiles';
import { KOLOBOK_ICQ_STEM_SET, kolobokIcqGifUri } from '../../util/kolobokSmiles';

/**
 * Подпись под чатом: одна и та же в строке списка и в карточке «Избранного».
 *
 * Раньше эта логика жила прямо в JSX `ChatListScreen`, и карточка «Избранного» печатала подпись
 * голым `<Text>` — поэтому в ней было видно служебное «[image]», а стикеры-маркеры не появлялись
 * вовсе. Общий компонент убирает саму возможность такого расхождения.
 */

/** Пульсирующий глиф типа вложения вместо служебного литерала. */
function MediaTypeIcon({
  kind,
  animated,
  style,
}: {
  kind: MediaPreviewKind;
  animated: boolean;
  /** Стиль подписи: глиф стоит на её месте и должен получать те же отступы и цвет. */
  style: StyleProp<TextStyle>;
}) {
  const [opacity] = useState(() => new Animated.Value(1));
  useEffect(() => {
    if (!animated) {
      opacity.setValue(1);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.4, duration: 1400, useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 1.0, duration: 1400, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [animated, opacity]);
  return <Animated.Text style={[style, { opacity }]}>{MEDIA_PREVIEW_GLYPH[kind]}</Animated.Text>;
}

/**
 * Картинка смайла из маркера `[[kb:stem]]` / `[[hds:stem]]`. Неизвестный набор — не картинка,
 * а `null`: показывать битую ссылку хуже, чем не показывать ничего.
 */
function renderPreviewMarker(marker: ChatPreviewMarker | null, styles: ChatListStyles) {
  if (!marker) return null;
  if (marker.kind === 'hds') {
    const source = darkSkinGifSource(marker.stem);
    if (!source) return null;
    return <Image source={source} style={styles.itemPreviewSticker} resizeMode="contain" />;
  }
  if (marker.kind === 'kb' && KOLOBOK_ICQ_STEM_SET.has(marker.stem)) {
    return <Image source={{ uri: kolobokIcqGifUri(marker.stem) }} style={styles.itemPreviewSticker} resizeMode="contain" />;
  }
  return null;
}

/**
 * Рядом с текстом может стоять картинка смайла, и без `flexShrink` длинная подпись выталкивала
 * бы её за край карточки вместо того, чтобы обрезаться многоточием.
 */
const PREVIEW_TEXT_SHRINK: TextStyle = { flexShrink: 1 };

export function ChatPreviewLine({
  preview,
  styles,
  numberOfLines = 1,
  animated = true,
  iconFontSize = CONTROL.glyph,
}: {
  preview: ResolvedChatPreview;
  styles: ChatListStyles;
  numberOfLines?: number;
  /**
   * Пульсация глифа. В строке списка она живёт внутри ячейки FlatList и исчезает вместе с ней,
   * а карточка «Избранного» приколота сверху навсегда — вечный `Animated.loop` там ни к чему.
   */
  animated?: boolean;
  /** Размер глифа — общий с текстовыми глифами кнопок (▾, ×). */
  iconFontSize?: number;
}) {
  const kind = mediaPreviewKind(preview.text);
  if (kind) {
    // Считаем маркер ПОСЛЕ раннего возврата: иначе на каждую медиа-строку списка создавался
    // и тут же выбрасывался <Image>, причём на каждую перерисовку каждой видимой ячейки.
    return (
      <MediaTypeIcon kind={kind} animated={animated} style={[styles.itemPreview, { fontSize: iconFontSize }]} />
    );
  }
  const markerImage = renderPreviewMarker(preview.marker, styles);
  if (preview.text.length > 0) {
    return (
      <View style={styles.itemPreviewRow}>
        <Text style={[styles.itemPreview, PREVIEW_TEXT_SHRINK]} numberOfLines={numberOfLines}>
          {preview.text}
        </Text>
        {markerImage}
      </View>
    );
  }
  if (markerImage) return markerImage;
  return (
    <Text style={styles.itemPreview} numberOfLines={numberOfLines}>
      {preview.text}
    </Text>
  );
}
